/**
 * Infinite scroll v3 for Taiga collection & search grids (Claudio Lugli).
 * Self-contained: injects its own CSS, so it can load either as a theme asset
 * (via snippets/pagination.liquid include) or via a storefront Script Tag.
 * Guarded against double-initialisation if both load paths are active.
 *
 * Performance strategy:
 *  1. SECTION RENDERING API — fetches only the product-grid section (?section_id=...).
 *  2. PREFETCH-AHEAD — next page fetched in idle time before the user needs it.
 *  3. CHUNKED REVEAL — products revealed REVEAL_BATCH at a time; pure DOM appends.
 * Progressive enhancement: crawlable Load-more link, facets-aware, URL replaceState,
 * classic pagination restored on any failure.
 */
(function () {
  'use strict';
  if (window.__clInfiniteScroll) return; // double-load guard
  window.__clInfiniteScroll = true;

  var GRID = '#product-grid-ajax';
  var PAGINATION = '#pagination-ajax';
  var NEXT_LINK = '.arrow-link--next[href]';
  var PREFETCH_MARGIN = '900px';
  var REVEAL_BATCH = 12;
  var MAX_AUTO_PAGES = 30;

  var CSS = '.cl-infinite-hidden{position:absolute!important;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}' +
    '#cl-infinite-wrap{text-align:center;margin:40px 0 56px}' +
    '#cl-infinite-sentinel{height:1px}' +
    '#cl-infinite-more{min-width:220px}' +
    '#cl-infinite-more.cl-loading{pointer-events:none;opacity:.55;position:relative}' +
    '#cl-infinite-more.cl-loading::after{content:"";display:inline-block;width:14px;height:14px;margin-left:10px;vertical-align:-2px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:cl-spin .7s linear infinite}' +
    '.cl-infinite-status{min-height:0;font-size:0}' +
    '.cl-infinite-end{color:rgba(0,0,0,.55);letter-spacing:.06em;text-transform:uppercase}' +
    '@keyframes cl-spin{to{transform:rotate(360deg)}}' +
    '@media(prefers-reduced-motion:reduce){#cl-infinite-more.cl-loading::after{animation:none}}';

  var state = { loading: false, autoLoads: 0, observer: null, queue: [], nextUrl: null, prefetched: null, prefetching: false };

  function qs(sel, root) { return (root || document).querySelector(sel); }

  function injectCss() {
    if (document.getElementById('cl-infinite-css')) return;
    var s = document.createElement('style');
    s.id = 'cl-infinite-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function sectionId() {
    var grid = qs(GRID);
    return grid ? grid.getAttribute('data-id') : null;
  }

  function readNextUrlFrom(navRoot) {
    var next = qs(NEXT_LINK, navRoot || qs(PAGINATION));
    return next ? next.getAttribute('href') : null;
  }

  function sectionUrl(url) {
    var id = sectionId();
    if (!id) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(id);
  }

  function fetchPage(url) {
    return fetch(sectionUrl(url)).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    });
  }

  function parsePage(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var grid = doc.querySelector(GRID);
    var nav = doc.querySelector(PAGINATION);
    if (!grid) throw new Error('grid missing in response');
    return { cards: Array.prototype.slice.call(grid.children), nextUrl: nav ? readNextUrlFrom(nav) : null };
  }

  function prefetchNext() {
    if (!state.nextUrl || state.prefetching) return;
    if (state.prefetched && state.prefetched.url === state.nextUrl) return;
    state.prefetching = true;
    var url = state.nextUrl;
    var run = function () {
      fetchPage(url).then(function (html) {
        state.prefetched = { url: url, html: html };
      }).catch(function () { })
        .then(function () { state.prefetching = false; });
    };
    if ('requestIdleCallback' in window) { requestIdleCallback(run, { timeout: 2000 }); } else { setTimeout(run, 200); }
  }

  function revealBatch() {
    if (!state.queue.length) return false;
    var grid = qs(GRID);
    if (!grid) return false;
    var frag = document.createDocumentFragment();
    state.queue.splice(0, REVEAL_BATCH).forEach(function (el) { frag.appendChild(el); });
    grid.appendChild(frag);
    updateButton();
    return true;
  }

  function updateButton() {
    var btn = document.getElementById('cl-infinite-more');
    var wrap = document.getElementById('cl-infinite-wrap');
    if (!wrap) return;
    var exhausted = !state.queue.length && !state.nextUrl;
    if (exhausted) {
      if (state.observer) { state.observer.disconnect(); state.observer = null; }
      wrap.innerHTML = '<p class="cl-infinite-end size--tiny" role="status">You’ve seen everything in this collection.</p>';
    } else if (btn && state.nextUrl) {
      btn.setAttribute('href', state.nextUrl);
    }
  }

  function advance() {
    if (state.loading) return;
    if (revealBatch()) {
      if (state.queue.length < REVEAL_BATCH) prefetchNext();
      state.autoLoads += 1;
      return;
    }
    if (!state.nextUrl) return;
    setBusy(true);
    var url = state.nextUrl;
    var htmlPromise = (state.prefetched && state.prefetched.url === url)
      ? Promise.resolve(state.prefetched.html)
      : fetchPage(url);
    state.prefetched = null;
    htmlPromise.then(function (html) {
      var page = parsePage(html);
      state.queue = state.queue.concat(page.cards);
      state.nextUrl = page.nextUrl;
      try { window.history.replaceState('', '', url); } catch (e) { }
      setBusy(false);
      revealBatch();
      state.autoLoads += 1;
      prefetchNext();
      updateButton();
    }).catch(function () {
      setBusy(false);
      var nav = qs(PAGINATION);
      var wrap = document.getElementById('cl-infinite-wrap');
      if (nav) nav.classList.remove('cl-infinite-hidden');
      if (wrap) wrap.remove();
      if (state.observer) { state.observer.disconnect(); state.observer = null; }
    });
  }

  function setBusy(busy) {
    state.loading = busy;
    var btn = document.getElementById('cl-infinite-more');
    var status = qs('.cl-infinite-status');
    if (btn) {
      btn.classList.toggle('cl-loading', busy);
      btn.setAttribute('aria-disabled', busy ? 'true' : 'false');
    }
    if (status) status.textContent = busy ? 'Loading more products…' : '';
  }

  function buildLoader() {
    var nav = qs(PAGINATION);
    if (!nav) return;
    var old = document.getElementById('cl-infinite-wrap');
    if (old) old.remove();
    if (state.observer) { state.observer.disconnect(); state.observer = null; }
    state.queue = [];
    state.prefetched = null;
    state.prefetching = false;
    state.autoLoads = 0;
    state.nextUrl = readNextUrlFrom(nav);
    if (!state.nextUrl) return;
    injectCss();
    nav.classList.add('cl-infinite-hidden');
    var wrap = document.createElement('div');
    wrap.id = 'cl-infinite-wrap';
    wrap.innerHTML =
      '<div id="cl-infinite-sentinel" aria-hidden="true"></div>' +
      '<div class="cl-infinite-status" role="status" aria-live="polite"></div>' +
      '<a id="cl-infinite-more" class="btn btn--secondary" href="' + state.nextUrl + '" rel="next">' +
        (window.themeStrings && window.themeStrings.load_more ? window.themeStrings.load_more : 'Load more') +
      '</a>';
    nav.parentNode.insertBefore(wrap, nav);
    document.getElementById('cl-infinite-more').addEventListener('click', function (e) {
      e.preventDefault();
      advance();
    });
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && state.autoLoads < MAX_AUTO_PAGES) advance();
        });
      }, { rootMargin: PREFETCH_MARGIN + ' 0px' });
      state.observer.observe(document.getElementById('cl-infinite-sentinel'));
    }
    prefetchNext();
  }

  function init() {
    if (!qs(GRID)) return;
    buildLoader();
    var nav = qs(PAGINATION);
    if (nav && 'MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].type === 'childList') { buildLoader(); break; }
        }
      }).observe(nav, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();